// Comfort & accessibility (owner-approved 2026-11-02): ONE place that decides how bright / how often anything may flash,
// how far the camera may shake and which colours carry enemy-attack meaning. Pure TypeScript (no three, no DOM) so the
// rules are unit-tested (tests/a11y.test.ts). Render modules import the singletons below; they never decide gameplay.
//
// Flash sources route through FLASH: fx.ts primitives (impact lights, flash stars, flash spheres, lightning arcs),
// lightning.ts / chainBolt.ts (flicker + re-strike), groundGlow.ts landing flashes, auraV3.ts ignition flare, view.ts
// enemy hit flashes and telegraph glow pulses, enemyDeaths.ts death pop, main.ts bloom + the full-screen impact dim,
// hud.ts hurt vignette. Colour meaning routes through CB (enemy telegraphs, hostile shots, damage numbers, gems).

// ---------------------------------------------------------------------------------------------------------------------
// Reduce flashing: Off (the game as authored) / Reduced / Minimal
// ---------------------------------------------------------------------------------------------------------------------
export type FlashMode = 'off' | 'reduced' | 'minimal';
export const FLASH_MODES: readonly FlashMode[] = ['off', 'reduced', 'minimal'];

export interface FlashLevel {
  /** brightness multiplier for every flash that is allowed through */
  dim: number;
  /** big flashes (screen-lighting point lights, large white stars, flash spheres, ignition flares, impact dim) per rolling second */
  bigPerSec: number;
  /** share of `dim` a big flash keeps once the per-second budget is spent (0 = dropped) */
  demote: number;
  /** extra multiplier for white / near-white flash stars (white is what reads as a strobe) */
  white: number;
  /** bloom strength multiplier */
  bloom: number;
  /** the fastest any glow / telegraph pulse may oscillate (Hz) */
  maxHz: number;
  /** amplitude multiplier for flicker (random per-frame shimmer, pulsing glows) */
  flicker: number;
  /** how far an enemy's white hit flash goes (0 = no flash) */
  hit: number;
  /** lightning brightness multiplier */
  bolt: number;
  /** lightning re-strikes (the second bolt a few frames later) */
  restrike: boolean;
}

/** WCAG 2.3.1 "three flashes" is the ceiling for Reduced; Minimal keeps one obvious flash a second at a quarter brightness. */
export const FLASH_LEVELS: Record<FlashMode, FlashLevel> = {
  off: { dim: 1, bigPerSec: Infinity, demote: 1, white: 1, bloom: 1, maxHz: Infinity, flicker: 1, hit: 1, bolt: 1, restrike: true },
  reduced: { dim: 0.5, bigPerSec: 3, demote: 0.2, white: 0.6, bloom: 0.55, maxHz: 2.5, flicker: 0.5, hit: 0.45, bolt: 0.7, restrike: false },
  minimal: { dim: 0.25, bigPerSec: 1, demote: 0, white: 0.3, bloom: 0.3, maxHz: 1.25, flicker: 0.2, hit: 0.15, bolt: 0.45, restrike: false },
};

/** Thresholds that make a primitive's flash "big" (counted against the per-second budget). */
export const BIG = { light: 20, star: 2, whiteStar: 1.5, sphere: 1.2, ground: 4 } as const;

export class FlashLimiter {
  mode: FlashMode = 'off';
  L: FlashLevel = FLASH_LEVELS.off;
  /** = L.dim (kept as a plain number: older call sites test `FLASH.dim >= 1` to skip optional lights) */
  dim = 1;
  private t = 0;
  private stamps: number[] = [];
  /** QA counters (window.__autotest in ?a11ycheck) */
  stats = { big: 0, passed: 0, demoted: 0 };

  setMode(m: FlashMode): void {
    this.mode = FLASH_LEVELS[m] ? m : 'off'; this.L = FLASH_LEVELS[this.mode]; this.dim = this.L.dim; this.stamps.length = 0;
  }
  /** advance the limiter clock (render seconds; frozen screens pass 0) */
  tick(dt: number): void { if (dt > 0) this.t += dt; }
  get now(): number { return this.t; }
  /** big flashes let through in the last second */
  get recent(): number { this.prune(); return this.stamps.length; }
  private prune(): void { while (this.stamps.length && this.t - this.stamps[0] >= 1 - 1e-9) this.stamps.shift(); }

  /** A big flash wants to fire now: the brightness multiplier to use. Within the budget it gets the level's dim;
   *  past it, the demoted share (0 = drop it). Off always returns 1 (nothing is counted against anything). */
  big(): number {
    this.stats.big++;
    if (this.mode === 'off') { this.stats.passed++; return 1; }
    this.prune();
    if (this.stamps.length < this.L.bigPerSec) { this.stamps.push(this.t); this.stats.passed++; return this.L.dim; }
    this.stats.demoted++;
    return this.L.dim * this.L.demote;
  }
  /** big ? the budgeted multiplier : the plain level dim */
  gate(isBig: boolean): number { return isBig ? this.big() : this.L.dim; }
  /** an oscillation rate (radians / s) capped to the level's max frequency */
  rate(radPerSec: number): number { return Math.min(radPerSec, this.L.maxHz * 2 * Math.PI); }
  /** a flicker / pulse amplitude */
  flick(amp: number): number { return amp * this.L.flicker; }
}

/** The one limiter every flash source asks. */
export const FLASH = new FlashLimiter();

/** true when a colour is white enough to read as a strobe (all channels high) */
export function isWhiteish(hex: number): boolean {
  const r = (hex >> 16) & 255, g = (hex >> 8) & 255, b = hex & 255;
  return Math.min(r, g, b) >= 200;
}

// ---------------------------------------------------------------------------------------------------------------------
// Screen shake: 0..100 % slider (applied once, where the view consumes fx.shake)
// ---------------------------------------------------------------------------------------------------------------------
/** camera offset amplitude (m) for a shake level 0..1 and a player scale 0..1 (the view's long-standing curve: k² × 0.7) */
export function shakeAmplitude(shake: number, scale: number): number {
  const s = Math.max(0, Math.min(1, shake)), k = Math.max(0, Math.min(1, scale));
  return s * s * SHAKE_MAX * k;
}
/** peak camera offset (m) at full shake, 100 %. Owner 2026-11-02: 100 % was unbearable at 0.7 — halved, and view.ts now
 *  moves the camera along a smooth rumble (summed sines) instead of a new random jump every frame */
export const SHAKE_MAX = 0.32;
/** smooth rumble direction at time t (each axis roughly -1..1), deterministic, no per-frame randomness */
export function shakeWave(t: number, out: { x: number; y: number; z: number }): void {
  out.x = Math.sin(t * 37.1) * 0.6 + Math.sin(t * 23.3 + 1.7) * 0.4;
  out.y = Math.sin(t * 31.7 + 0.6) * 0.6 + Math.sin(t * 19.9 + 2.9) * 0.4;
  out.z = Math.sin(t * 29.3 + 4.1) * 0.6 + Math.sin(t * 41.9 + 0.3) * 0.4;
}

// ---------------------------------------------------------------------------------------------------------------------
// Colour-blind safe mode: Off / Protan-Deutan (red-green) / Tritan (blue-yellow)
// Palette swaps for every colour that carries ENEMY-ATTACK meaning, plus shape cues drawn by telegraphs.ts (chevron
// lanes, dashed slam rings, ringed hostile shots, a "!" over a winding-up foe) so meaning never rests on hue alone.
// Skill colours stay the owner's; only colours that collide with telegraph meaning are swapped.
// ---------------------------------------------------------------------------------------------------------------------
export type CbMode = 'off' | 'pd' | 'tritan';
export const CB_MODES: readonly CbMode[] = ['off', 'pd', 'tritan'];

/** Every colour role with gameplay meaning. `view.ts` / `telegraphs.ts` / `hud` read these instead of literals. */
export const TELEGRAPH_KEYS = [
  'chargeLane',   // lancer wind-up: the lane it will charge down
  'slamCircle',   // stomper wind-up: the ring it will slam
  'bossCharge',   // boss charge lane
  'bossRing',     // boss ring-burst wind-up (hostile shots follow)
  'bossAura',     // boss sparks
  'chargeCue',    // the pop when a lancer starts its wind-up
  'hostileShot',  // enemy projectile body / halo
  'hostileGlow',  // ground glow under enemy projectiles
  'bossTrail',    // explosions along the boss charge path
  'bossBurst',    // the boss ring-burst release
  'bomberPop',    // bomber self-destruct
  'stompWave',    // stomper slam shock ring
  'playerHurt',   // the hero was hit
  'hazardCrush',  // map hazard: crushing zone (warn → one big hit)
  'hazardBurn',   // map hazard: burning ground
  'hazardSlow',   // map hazard: slowing zone
  'hazardSlip',   // map hazard: slippery zone
] as const;
export type TelegraphKey = typeof TELEGRAPH_KEYS[number];

export const PICKUP_KEYS = ['xpSmall', 'xpMedium', 'xpLarge', 'salve', 'seal', 'sweep', 'xpHuge'] as const; // xpHuge: render-only 4th XP tier (gemTier.ts)
export type PickupKey = typeof PICKUP_KEYS[number];

export interface CbPalette {
  telegraph: Record<TelegraphKey, number>;
  /** classic gem path (sim gem kinds 0..5 → small, medium, large, salve, seal, sweep); xpHuge = a Large gem worth 40+ XP (gemTier.ts) */
  pickup: Record<PickupKey, number>;
  /** damage numbers: fill colours (CSS) */
  dmg: { normal: string; crit: string };
}

const OFF: CbPalette = {
  telegraph: {
    chargeLane: 0xff3040, slamCircle: 0xff5030, bossCharge: 0xff2040, bossRing: 0xff30c0, bossAura: 0xff30a0, chargeCue: 0xff3040,
    hostileShot: 0xff1ad0, hostileGlow: 0xff30c0, bossTrail: 0xff4060, bossBurst: 0xff3af0, bomberPop: 0xffa020, stompWave: 0xffa070,
    playerHurt: 0xff5050, hazardCrush: 0xff2438, hazardBurn: 0xff5a1e, hazardSlow: 0x5ac8ff, hazardSlip: 0xa8ecff, // (hazards: render/hazardView.ts)
  },
  // XP by value (owner 2026-11-02): blue → violet → magenta → red (+ gold glint). Hue, size, silhouette and glow all step up together.
  // 2026-11-04 (owner, P1's palette for bright maps): the old cyan-blue / green / violet vanished on light grass and sand; now royal
  // sapphire #2455DF / electric amethyst #7022CF / radiant fuchsia #CB146C (no green or yellow, which disappear on bright terrain).
  pickup: { xpSmall: 0x2455df, xpMedium: 0x7022cf, xpLarge: 0xcb146c, xpHuge: 0xff2a36, salve: 0xff2a40, seal: 0xffb020, sweep: 0x22c0ff },
  dmg: { normal: '#ffffff', crit: '#ffe060' },
};

/** Protan / Deutan: red and green collapse to olive-yellow, so danger moves to magenta + white (reads as blue-violet
 *  against the yellow-olive grass for both), pickups use the Okabe-Ito blue / yellow / reddish-purple. */
const PD: CbPalette = {
  telegraph: {
    chargeLane: 0xff38ff, slamCircle: 0xff38ff, bossCharge: 0xff38ff, bossRing: 0xff60ff, bossAura: 0xd070ff, chargeCue: 0xffffff,
    hostileShot: 0xff40ff, hostileGlow: 0xd040ff, bossTrail: 0xe060ff, bossBurst: 0xff60ff, bomberPop: 0xffffff, stompWave: 0xffffff,
    playerHurt: 0xff40ff, hazardCrush: 0xff38ff, hazardBurn: 0xd878ff, hazardSlow: 0x3a7cff, hazardSlip: 0xb8e8ff,
  },
  pickup: { xpSmall: 0x3a9cff, xpMedium: 0xf0e442, xpLarge: 0xcc79a7, xpHuge: 0xffffff, salve: 0xffffff, seal: 0xe69f00, sweep: 0x56b4e9 },
  dmg: { normal: '#ffffff', crit: '#ffb000' },
};

/** Tritan: blue and yellow / green collapse, red stays red. Danger is a strong crimson + white, crits turn pink so they
 *  never rely on yellow-vs-white. */
const TRITAN: CbPalette = {
  telegraph: {
    chargeLane: 0xff1030, slamCircle: 0xff1030, bossCharge: 0xff1030, bossRing: 0xff2050, bossAura: 0xff3060, chargeCue: 0xffffff,
    hostileShot: 0xff1040, hostileGlow: 0xff2050, bossTrail: 0xff2040, bossBurst: 0xff2050, bomberPop: 0xffffff, stompWave: 0xffffff,
    playerHurt: 0xff1030, hazardCrush: 0xff1030, hazardBurn: 0xff7a00, hazardSlow: 0x9a6aff, hazardSlip: 0xffffff,
  },
  pickup: { xpSmall: 0x00c8c8, xpMedium: 0xff5a8a, xpLarge: 0xffffff, xpHuge: 0xff1030, salve: 0xff1030, seal: 0xff8040, sweep: 0x60f0ff },
  dmg: { normal: '#ffffff', crit: '#ff5c8a' },
};

export const CB_PALETTES: Record<CbMode, CbPalette> = { off: OFF, pd: PD, tritan: TRITAN };

/** sim-chosen explosion colours that mean "enemy attack" (src/core/sim.ts) → their palette role */
export const HOSTILE_EVENT_COLORS: Record<number, TelegraphKey> = { 0xff4060: 'bossTrail', 0xff3af0: 'bossBurst', 0xffa020: 'bomberPop' };

export class ColorBlind {
  mode: CbMode = 'off';
  P: CbPalette = OFF;
  set(m: CbMode): void { this.mode = CB_PALETTES[m] ? m : 'off'; this.P = CB_PALETTES[this.mode]; }
  /** shape cues (telegraphs.ts) are drawn whenever a colour-blind mode is on */
  get cues(): boolean { return this.mode !== 'off'; }
  tc(k: TelegraphKey): number { return this.P.telegraph[k]; }
  pickup(k: PickupKey): number { return this.P.pickup[k]; }
  /** an explosion colour from the sim: swapped when it is a hostile-attack colour, untouched otherwise (skills keep theirs) */
  remap(hex: number): number { const k = HOSTILE_EVENT_COLORS[hex]; return k ? this.P.telegraph[k] : hex; }
}

export const CB = new ColorBlind();
